// Node definitions with icons as React components
import React from 'react';
import { 
  PlayCircleOutlined,
  StopOutlined,
  FormOutlined,
  FileTextOutlined,
  FileExcelOutlined,
  TableOutlined,
  FilterOutlined,
  FunctionOutlined,
  CompressOutlined,
  ScissorOutlined,
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  SortAscendingOutlined,
  GroupOutlined,
  RotateRightOutlined,
  MergeCellsOutlined,
  WindowsOutlined,
  BarChartOutlined,
  BranchesOutlined,
  MergeOutlined,
  RedoOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  TagOutlined,
  MessageOutlined,
  FontSizeOutlined,
  BorderOutlined,
  CodeOutlined,
  ApiOutlined,
  FileMarkdownOutlined,
  BlockOutlined,
  CaretRightOutlined,
  CheckSquareOutlined,
  
} from '@ant-design/icons';
import { IfElseIcon } from '../../../utils/icons';

export const NODE_DEFINITIONS = {
  // Flow Control
  start: {
    name: 'Start',
    icon: <PlayCircleOutlined />,
    description: 'Start point of the flow execution',
    inputs: [],
    outputs: ['flow']
  },
  end: {
    name: 'End', 
    icon: <StopOutlined />,
    description: 'End point of the flow execution',
    inputs: ['flow'],
    outputs: []
  },
  
  // Input nodes
  input: {
    name: 'Field Input',
    icon: <FormOutlined />,
    description: 'Dynamic input field with configurable types',
    inputs: ['flow'],
    outputs: ['data']
  },
  csv_input: {
    name: 'CSV Input',
    icon: <TableOutlined />,
    description: 'Upload and analyze CSV files with schema detection',
    inputs: ['flow'],
    outputs: ['dataset']
  },
  excel_input: {
    name: 'Excel Input',
    icon: <FileExcelOutlined style={{color:'#73d13d'}}/>,
    description: 'Upload and analyze Excel/CSV files with multi-sheet support',
    inputs: ['flow'],
    outputs: ['dataset']
  },
  table: {
    name: 'Table',
    icon: <TableOutlined />,
    description: 'Display and select data schema from Excel input nodes',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  
  // Filter nodes
  filter_rows: {
    name: 'Filter Rows',
    icon: <FilterOutlined />,
    description: 'Filter rows by condition',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  filter_columns: {
    name: 'Select Columns',
    icon: <FilterOutlined />,
    description: 'Select specific columns',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  if_else: {
    name: 'If-Else',
    icon: <IfElseIcon />,
    description: 'Conditional branch',
    inputs: ['condition'],
    outputs: ['true', 'false']
  },
  
  // Transform basic nodes
  map: {
    name: 'Map',
    icon: <FunctionOutlined />,
    description: 'Transform each record using function',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  reduce: {
    name: 'Reduce',
    icon: <FunctionOutlined />,
    description: 'Aggregate dataset into single value',
    inputs: ['dataset'],
    outputs: ['result']
  },
  slice: {
    name: 'Slice/Limit',
    icon: <ScissorOutlined />,
    description: 'Extract subset of rows',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  distinct: {
    name: 'Distinct',
    icon: <FilterOutlined />,
    description: 'Remove duplicate rows',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  
  // Calculation nodes
  calculate_field: {
    name: 'Calculate Field',
    icon: <FunctionOutlined />,
    description: 'Create calculated field',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  add_column: {
    name: 'Add Column',
    icon: <PlusOutlined />,
    description: 'Add new column with formula calculation',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  remove_column: {
    name: 'Remove Column',
    icon: <MinusOutlined />,
    description: 'Remove selected columns from dataset',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  rename_column: {
    name: 'Rename Column',
    icon: <EditOutlined />,
    description: 'Rename column headers',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  sort: {
    name: 'Sort Data',
    icon: <SortAscendingOutlined />,
    description: 'Sort by columns',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  // Transform advanced nodes
  pivot: {
    name: 'Pivot/Unpivot',
    icon: <TableOutlined />,
    description: 'Transform between wide and long format',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  join: {
    name: 'Join',
    icon: <MergeCellsOutlined />,
    description: 'Join two datasets',
    inputs: ['left', 'right'],
    outputs: ['joined']
  },
  merge: {
    name: 'Merge',
    icon: <MergeCellsOutlined />,
    description: 'Merge multiple datasets',
    inputs: ['dataset_1', 'dataset_2'],
    outputs: ['dataset']
  },
  window_function: {
    name: 'Window Function',
    icon: <WindowsOutlined />,
    description: 'Apply window functions (rank, lag, etc.)',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  group_by: {
    name: 'Group By',
    icon: <BarChartOutlined />,
    description: 'Group data and calculate totals',
    inputs: ['dataset'],
    outputs: ['dataset']
  },
  
  // Logic nodes
  range_branch: {
    name: 'Range Branch',
    icon: <BranchesOutlined />,
    description: 'Branch by numeric range',
    inputs: ['value'],
    outputs: ['low', 'medium', 'high']
  },
  switch_case: {
    name: 'Switch/Case',
    icon: <BranchesOutlined />,
    description: 'Multi-way conditional branch',
    inputs: ['data'],
    outputs: ['case_1', 'case_2', 'case_3', 'default']
  },
  merge_flow: {
    name: 'Merge Flow',
    icon: <MergeOutlined />,
    description: 'Combine multiple flow paths',
    inputs: ['flow_1', 'flow_2', 'flow_3'],
    outputs: ['flow']
  },
  loop: {
    name: 'Loop',
    icon: <RedoOutlined />,
    description: 'Iterate over data or conditions',
    inputs: ['dataset'],
    outputs: ['loop_data']
  },
  batch_start: {
    name: 'Batch Start',
    icon: <BlockOutlined style={{color: '#722ed1'}}/>,
    description: 'Start batch processing - runs downstream nodes for each input dataset',
    inputs: ['dataset'],  // Dynamic - will have multiple inputs
    outputs: ['flow'],  // Single flow output to connect to batch processing nodes
    hasDynamicInputs: true,  // Flag for dynamic input handles
    category: 'batch'
  },
  batch_end: {
    name: 'Batch End',
    icon: <CheckSquareOutlined style={{color: '#722ed1'}}/>,
    description: 'End batch processing - collects results from all iterations',
    inputs: ['dataset'],  // Single input - result from batch scope
    outputs: ['dataset'],  // Dynamic - will have multiple outputs matching batch_start inputs
    hasDynamicOutputs: true,  // Flag for dynamic output handles
    category: 'batch'
  },
  wait: {
    name: 'Wait',
    icon: <ClockCircleOutlined />,
    description: 'Pause execution with conditions',
    inputs: ['flow'],
    outputs: ['flow']
  },
  
  // Output nodes
  dataset_output: {
    name: 'Dataset Output',
    icon: <DatabaseOutlined />,
    description: 'Export dataset',
    inputs: ['dataset'],
    outputs: ['flow']
  },

  export_excel: {
    name: 'Excel Export',
    icon: <FileExcelOutlined style={{color:'#73d13d'}}/>,
    description: 'Export data to Excel file with multi-sheet support',
    inputs: ['dataset'],
    outputs: ['flow']
  },
  set_dataset_name: {
    name: 'Set Dataset Name',
    icon: <TagOutlined />,
    description: 'Tag dataset with a name for Excel sheets',
    inputs: ['dataset'],
    outputs: ['dataset']
  },

  // Decoration nodes - purely visual, no effect on workflow execution
  comment: {
    name: 'Comment',
    icon: <MessageOutlined />,
    description: 'Add a comment bubble to document your workflow',
    inputs: [],
    outputs: [],
    isDecoration: true
  },
  text_annotation: {
    name: 'Text',
    icon: <FontSizeOutlined />,
    description: 'Add text annotation to label sections',
    inputs: [],
    outputs: [],
    isDecoration: true
  },
  shape_rectangle: {
    name: 'Rectangle',
    icon: <BorderOutlined />,
    description: 'Add a rectangle shape for grouping nodes',
    inputs: [],
    outputs: [],
    isDecoration: true
  }
};

// Helper function to get icon for a node type
export const getNodeIcon = (nodeType) => {
  const nodeDef = NODE_DEFINITIONS[nodeType];
  return nodeDef?.icon || <CodeOutlined />;
};

// Node categories for the palette
export const NODE_CATEGORIES = {
  'DECORATIONS': {
    color: '#8c8c8c',
    nodes: ['comment', 'text_annotation', 'shape_rectangle']
  },
  'FLOW CONTROL': {
    color: '#52c41a',
    nodes: ['start', 'end']
  },
  'STORES INPUT & OUTPUT': {
    color: '#13c2c2',
    nodes: ['input', 'excel_input', 'csv_input', 'dataset_output', 'export_excel']
  },
  'STORES MODIFICATION': {
    color: '#fa541c',
    nodes: ['table','set_dataset_name']
  },
  'FILTERING & SELECTION': {
    color: '#1890ff',
    nodes: ['filter_rows', 'filter_columns', 'slice', 'distinct']
  },
  'BASIC TRANSFORMATION': {
    color: '#fa8c16',
    nodes: ['map', 'add_column', 'remove_column', 'rename_column', 'sort']
  },
  'ADVANCED TRANSFORMATION': {
    color: '#eb2f96',
    nodes: ['group_by', 'pivot', 'join', 'merge', 'window_function']
  },
  'LOGIC & BRANCHING': {
    color: '#722ed1',
    nodes: ['if_else', 'range_branch', 'merge_flow', 'loop', 'batch_start', 'batch_end']
  }
};
